import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, MapPin, ArrowRight, ChevronLeft, ChevronRight, Lock, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";

const WA = "https://wa.me/554135646160";
const TEL = "tel:+554135646160";

const CERTIFICATES_FALLBACK = [
  { img: "/cert1.png", label: "Hair Designer — Pivot Point" },
  { img: "/cert2.png", label: "Visagismo Masculino" },
  { img: "/cert3.png", label: "Formação de Colorista — Keune Academy" },
  { img: "/cert4.png", label: "Colorimetria Avançada — Colorimetrista" },
  { img: "/cert5.png", label: "Colorista Capilar Científico" },
];

const NAV = [
  { label: "Início", href: "#inicio" },
  { label: "Sobre", href: "#sobre" },
  { label: "Certificados", href: "#certificados" },
  { label: "Localização", href: "#localizacao" },
];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

const MaskedLine = ({ children, delay }) => (
  <span className="mask-line">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const SectionLabel = ({ children }) => (
  <p className="text-xs font-semibold tracking-[0.22em] uppercase text-[#C8A279] mb-4">{children}</p>
);

export default function Landing() {
  const [certs, setCerts] = useState(CERTIFICATES_FALLBACK);
  const [zoom, setZoom] = useState(null);
  const certRef = useRef(null);

  useEffect(() => {
    api
      .get("/certificates")
      .then((res) => {
        if (res.data?.length) {
          setCerts(res.data.map((c) => ({ img: c.url, label: c.title })));
        }
      })
      .catch(() => {});
  }, []);

  const zoomIdx = zoom ? certs.findIndex((c) => c.img === zoom.img) : -1;
  const moveZoom = (d) => {
    if (zoomIdx < 0) return;
    setZoom(certs[(zoomIdx + d + certs.length) % certs.length]);
  };

  useEffect(() => {
    if (!zoom) return undefined;
    const h = (e) => {
      if (e.key === "Escape") setZoom(null);
      if (e.key === "ArrowRight") moveZoom(1);
      if (e.key === "ArrowLeft") moveZoom(-1);
    };
    window.addEventListener("keydown", h);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", h);
      document.body.style.overflow = "";
    };
  }, [zoom, zoomIdx]);

  const scrollCerts = (dir) => {
    certRef.current?.scrollBy({ left: dir * 380, behavior: "smooth" });
  };

  return (
    <div className="bg-[#FAF9F5] text-[#121212] min-h-screen relative grain" data-testid="landing-page">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-[#FAF9F5]/85 border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-4">
          <a href="#inicio" className="flex items-center gap-3 shrink-0" data-testid="header-logo">
            <Logo size={34} />
            <span className="leading-none">
              <span className="font-display text-lg tracking-wide block">Marcio Pissaia</span>
              <span className="text-[10px] font-semibold tracking-[0.2em] uppercase text-stone-500">Cabeleireiro Unissex</span>
            </span>
          </a>
          <nav className="hidden lg:flex items-center gap-8 text-sm text-stone-600">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} className="hover:text-stone-900 transition-colors duration-200">{n.label}</a>
            ))}
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link
              to="/login"
              data-testid="header-login-button"
              className="flex items-center gap-2 text-sm px-3 sm:px-4 py-2 rounded-full border border-stone-300 text-stone-700 hover:border-stone-900 hover:text-stone-900 transition-colors duration-200"
            >
              <Lock size={13} /> <span className="hidden sm:inline">Acessar Sistema</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section id="inicio" className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 pt-14 pb-20 lg:pt-20 lg:pb-28">
          <div className="max-w-3xl">
            <h1 className="font-display font-light leading-[0.95] tracking-tight text-5xl sm:text-7xl lg:text-8xl">
              <MaskedLine delay={0.15}>Marcio</MaskedLine>
              <MaskedLine delay={0.3}>
                <span className="italic text-[#C8A279]">Pissaia</span>
              </MaskedLine>
            </h1>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.65, duration: 0.7 }}
              className="mt-8"
              data-testid="hero-credentials"
            >
              <p className="text-xs font-semibold tracking-[0.28em] uppercase text-[#C8A279]">Cabeleireiro Unissex</p>
              <ul className="mt-4 space-y-1.5">
                {["Visagista", "Especialista em Cortes", "Colorista & Colorimetrista", "Terapeuta Capilar Cosmético"].map((t) => (
                  <li key={t} className="flex items-center gap-3 text-sm sm:text-base text-stone-600">
                    <span className="w-1 h-1 rounded-full bg-[#C8A279] shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.7 }}
              className="mt-6 flex flex-wrap gap-3"
            >
              <a
                href={WA}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-whatsapp-button"
                className="group inline-flex items-center gap-2 bg-[#121212] text-white rounded-full px-7 py-3.5 text-sm font-medium hover:bg-stone-700 transition-colors duration-300"
              >
                Agendar pelo WhatsApp
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <a
                href={TEL}
                data-testid="hero-phone-button"
                className="inline-flex items-center gap-2 border border-stone-300 rounded-full px-7 py-3.5 text-sm font-medium text-stone-800 hover:border-stone-900 transition-colors duration-300"
              >
                <Phone size={16} className="text-[#C8A279]" /> (41) 3564-6160
              </a>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.7 }}
              className="mt-10 relative inline-block"
            >
              <div className="relative bg-white border border-stone-200 rounded-2xl px-8 py-6 shadow-[0_12px_30px_rgba(18,18,18,0.08)]" data-testid="horario-balao">
                <p className="text-[10px] font-semibold tracking-[0.24em] uppercase text-stone-400">Atendimento</p>
                <p className="font-display text-2xl mt-2">Terça a Sábado</p>
                <p className="font-display text-3xl text-[#C8A279] mt-1">09:00 às 19:00</p>
                <span className="absolute -top-2 left-10 w-4 h-4 bg-white border-t border-l border-stone-200 rotate-45" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="border-y border-stone-200/70 bg-[#F3EFE6] py-5 overflow-hidden" data-testid="marquee">
        <div className="flex whitespace-nowrap animate-marquee w-max">
          {[0, 1].map((copy) => (
            <div key={copy} className="flex items-center" aria-hidden={copy === 1}>
              {["Cortes Feminino", "Masculino", "Infantil", "Coloração", "Tratamentos Capilar", "Progressiva", "Botox", "Selagem", "Mechas"].map((word) => (
                <span key={`${copy}-${word}`} className="flex items-center">
                  <span className="font-display text-2xl lg:text-3xl font-light text-stone-800 px-6">{word}</span>
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C8A279]" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Sobre */}
      <section id="sobre" className="max-w-7xl mx-auto px-4 sm:px-8 py-20 lg:py-32">
        <Reveal>
          <SectionLabel>Sobre o salão</SectionLabel>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-tight">
            Um espaço pensado para o cuidado de <span className="italic text-[#C8A279]">todos os cabelos</span>
          </h2>
          <p className="mt-6 text-stone-600 leading-relaxed max-w-2xl">
            Atendimento personalizado, produtos de alta performance e um ambiente acolhedor para cuidar do seu visual.
          </p>
        </Reveal>
      </section>

      {/* Certificados */}
      <section id="certificados" className="py-20 lg:py-28 bg-[#F3EFE6] border-y border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <Reveal className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <SectionLabel>Certificados & Qualificações</SectionLabel>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight">
                Formação e <span className="italic text-[#C8A279]">especializações</span>
              </h2>
            </div>
            <div className="flex gap-2">
              <button onClick={() => scrollCerts(-1)} data-testid="certificates-prev-button" className="w-11 h-11 rounded-full border border-stone-300 flex items-center justify-center hover:bg-white transition-colors" aria-label="Anterior">
                <ChevronLeft size={18} />
              </button>
              <button onClick={() => scrollCerts(1)} data-testid="certificates-next-button" className="w-11 h-11 rounded-full border border-stone-300 flex items-center justify-center hover:bg-white transition-colors" aria-label="Próximo">
                <ChevronRight size={18} />
              </button>
            </div>
          </Reveal>
          <div ref={certRef} className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory thin-scroll" data-testid="certificates-carousel">
            {certs.map((cert, i) => (
              <motion.div
                key={cert.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: i * 0.12 }}
                className="snap-start shrink-0 w-[320px] sm:w-[400px] group"
                data-testid={`certificate-card-${i}`}
              >
                <div
                  className="relative overflow-hidden rounded-2xl shadow-lg bg-white border border-stone-200 cursor-zoom-in"
                  onClick={() => setZoom(cert)}
                  data-testid={`certificate-zoom-${i}`}
                  title="Clique para ampliar"
                >
                  <img src={cert.img} alt={cert.label} className="w-full h-[240px] sm:h-[280px] object-contain transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
                <p className="mt-4 text-sm font-medium text-stone-700">{cert.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Localização */}
      <section id="localizacao" className="relative overflow-hidden">
        <div className="bg-[#121212] text-[#FAF9F5] px-6 sm:px-12 lg:px-16 py-20 lg:py-24">
            <Reveal>
              <p className="text-xs font-semibold tracking-[0.22em] uppercase text-[#C8A279] mb-4">Localização</p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-tight">
                Venha nos <span className="italic text-[#C8A279]">visitar</span>
              </h2>
              <div className="mt-8 space-y-5 text-stone-300">
                <p className="flex items-start gap-3">
                  <MapPin size={18} className="text-[#C8A279] mt-0.5 shrink-0" />
                  Rua Deputado Pinheiro Junior 1221 - Loja 3
                </p>
                <p className="flex items-center gap-3">
                  <Phone size={18} className="text-[#C8A279] shrink-0" />
                  (41) 3564-6160
                </p>
                <p className="text-sm text-stone-400">Terça a sábado, das 9:00 às 19:00.</p>
              </div>
              <a
                href="https://www.google.com/maps/search/?api=1&query=Rua+Deputado+Pinheiro+Junior+1221+Curitiba+PR"
                target="_blank"
                rel="noreferrer"
                data-testid="location-maps-link"
                className="mt-4 inline-flex w-fit items-center gap-2 text-sm text-[#C8A279] hover:text-white transition-colors"
              >
                Abrir no Google Maps
                <ArrowRight size={14} />
              </a>
              <a
                href={WA}
                target="_blank"
                rel="noreferrer"
                data-testid="location-whatsapp-button"
                className="mt-10 inline-flex w-fit items-center gap-2 bg-[#C8A279] text-[#121212] rounded-full px-8 py-4 text-sm font-semibold hover:bg-[#B38F68] transition-colors duration-300"
              >
                Fale conosco no WhatsApp
                <ArrowRight size={16} />
              </a>
            </Reveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-stone-200/70 bg-[#FAF9F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src="/logo.png" alt="Marcio Pissaia — Cabeleireiro Unissex" className="h-16 sm:h-20 w-auto object-contain" data-testid="footer-logo" />
            <span className="hidden sm:block w-px h-10 bg-stone-200" />
            <span className="font-display text-lg hidden sm:block">Cabeleireiro Unissex · Curitiba</span>
          </div>
          <p className="text-xs text-stone-400">© {new Date().getFullYear()} · Curitiba — PR</p>
        </div>
      </footer>

      {/* Floating WhatsApp */}
      {/* Visualização ampliada do certificado */}
      {zoom && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-sm flex flex-col items-center justify-center p-4"
          onClick={() => setZoom(null)}
          data-testid="certificate-zoom-modal"
        >
          <button
            onClick={() => setZoom(null)}
            data-testid="certificate-zoom-close"
            aria-label="Fechar"
            className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors"
          >
            <X size={22} />
          </button>
          <motion.img
            key={zoom.img}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            src={zoom.img}
            alt={zoom.label}
            className="max-h-[78vh] max-w-[92vw] object-contain rounded-xl shadow-2xl bg-white"
            onClick={(e) => e.stopPropagation()}
            data-testid="certificate-zoom-image"
          />
          <p className="mt-4 text-white/90 text-sm font-medium">{zoom.label}</p>
          <p className="text-white/50 text-xs mb-4">
            {zoomIdx + 1} / {certs.length} — use as setas para navegar
          </p>
          <div className="flex gap-3" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => moveZoom(-1)}
              data-testid="certificate-zoom-prev"
              className="w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors"
              aria-label="Anterior"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={() => moveZoom(1)}
              data-testid="certificate-zoom-next"
              className="w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors"
              aria-label="Próximo"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </motion.div>
      )}
      <a
        href={WA}
        target="_blank"
        rel="noreferrer"
        data-testid="floating-whatsapp-button"
        aria-label="WhatsApp"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-xl hover:scale-110 transition-transform duration-300"
      >
        <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.347-.347.52-.52.174-.174.232-.298.347-.497.115-.198.058-.372-.029-.52-.086-.149-.66-1.59-.905-2.178-.238-.571-.48-.466-.66-.475l-.56-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
      </a>
    </div>
  );
}
